// ════════════════════════════════════════════════════════════════════════════
//  Transferências entre depósitos (WMS) — lista + criação + workflow
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, ArrowLeftRight, Trash2, Eye, Search } from 'lucide-react';
import { apiClient, getApiErrorMessage } from '@/lib/apiClient';
import { useDebounce } from '@/hooks/useDebounce';
import type { TamanhoPagina } from '@/hooks/usePaginacaoLocal';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import {
  transferenciaService as svc, MOTIVOS_DIVERGENCIA,
  type TransferenciaItem,
} from '@/features/wms/wmsService';
import { useLocalEstoqueCombo } from '@/features/estoque/hooks/useLocalEstoque';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STATUS_BADGE: Record<number, string> = {
  0: 'bg-blue-500/10 text-blue-600',
  1: 'bg-amber-500/10 text-amber-600',
  2: 'bg-indigo-500/10 text-indigo-600',
  3: 'bg-orange-500/10 text-orange-600',
  4: 'bg-green-500/10 text-green-600',
  5: 'bg-slate-500/10 text-slate-500',
};
const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const SENT = '__none__';

// Mesmos rótulos do enum StatusTransferencia (API).
const STATUS_OPCOES = [
  { id: 0, label: 'Emitida' },
  { id: 1, label: 'Aguardando aprovação' },
  { id: 2, label: 'Em trânsito' },
  { id: 3, label: 'Recebida parcialmente' },
  { id: 4, label: 'Recebida totalmente' },
  { id: 5, label: 'Cancelada' },
];

interface ItemDraft { produtoId: number; qtdEmitida: number; custoUnitEmissao: number; }

export default function Transferencias() {
  const qc = useQueryClient();
  const [novaOpen, setNovaOpen] = useState(false);
  const [detalheId, setDetalheId] = useState<number | null>(null);

  const { data: locais = [] } = useLocalEstoqueCombo();

  // Lista paginada no servidor (antes: só as 50 mais recentes, sem como chegar às anteriores).
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [statusId, setStatusId] = useState(-1);
  const [busca, setBusca] = useState('');
  const buscaEnviada = useDebounce(busca.trim(), 400);
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'valor'>('emissao', true, () => setPagina(1));
  const filtros = {
    busca: buscaEnviada, ...periodo.intervalo,
    ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
  };
  const { data: lista, isLoading, isFetching } = useQuery({
    queryKey: ['transferencias', pagina, tamanhoPagina, statusId, filtros],
    queryFn: () => svc.listar(pagina, tamanhoPagina, statusId, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada filtro
  });
  const total = lista?.total ?? 0;
  const totalPaginas = Math.max(1, lista?.totalPaginas ?? 1);

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['transferencias'] });
    if (detalheId) qc.invalidateQueries({ queryKey: ['transferencia', detalheId] });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Transferências entre Depósitos</h1>
          <p className="text-muted-foreground mt-1">Movimente saldo entre locais com em-trânsito, aprovação e conferência</p>
        </div>
        <Button className="gap-2" onClick={() => setNovaOpen(true)}><Plus className="h-4 w-4" />Nova Transferência</Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <CardTitle className="flex items-center gap-2 whitespace-nowrap"><ArrowLeftRight className="h-5 w-5" />Transferências</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Número ou observação..." value={busca} className="pl-9"
                  onChange={(e) => { setBusca(e.target.value); setPagina(1); }} />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
              <Select value={String(statusId)} onValueChange={(v) => { setStatusId(Number(v)); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-48" aria-label="Status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="-1">Todos os status</SelectItem>
                  {STATUS_OPCOES.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº</CabecalhoOrdenavel><TableHead>Origem</TableHead><TableHead>Destino</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Itens</TableHead>
              <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Valor</CabecalhoOrdenavel>
              <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Emissão</CabecalhoOrdenavel><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : !lista?.itens.length ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {periodo.periodo !== 'todos'
                    ? 'Nenhuma transferência neste período — amplie o período para ver as anteriores'
                    : 'Nenhuma transferência'}
                </TableCell></TableRow>
              ) : lista.itens.map(t => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">#{t.numero}</TableCell>
                  <TableCell>{t.localOrigem ?? t.localOrigemId}</TableCell>
                  <TableCell>{t.localDestino ?? t.localDestinoId}</TableCell>
                  <TableCell><Badge className={STATUS_BADGE[t.statusId]}>{t.status}</Badge></TableCell>
                  <TableCell className="text-right">{t.qtdItens}</TableCell>
                  <TableCell className="text-right">R$ {fmt(t.valorTotal)}</TableCell>
                  <TableCell>{new Date(t.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => setDetalheId(t.id)}><Eye className="h-4 w-4" />Abrir</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <PaginacaoRodape
            pagina={pagina}
            totalPaginas={totalPaginas}
            totalItens={total}
            primeiroItem={total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1}
            ultimoItem={Math.min(pagina * tamanhoPagina, total)}
            tamanhoPagina={tamanhoPagina}
            onPaginaChange={setPagina}
            onTamanhoPaginaChange={(t) => { setTamanhoPagina(t); setPagina(1); }}
          />
          {isFetching && !isLoading && <p className="text-xs text-muted-foreground">(atualizando...)</p>}
        </CardContent>
      </Card>

      {novaOpen && (
        <NovaTransferenciaDialog
          locais={locais}
          onClose={() => setNovaOpen(false)}
          onSaved={() => { setNovaOpen(false); invalidar(); }}
        />
      )}
      {detalheId && (
        <DetalheDialog id={detalheId} onClose={() => setDetalheId(null)} onChanged={invalidar} />
      )}
    </div>
  );
}

// ── Nova transferência ─────────────────────────────────────────────────────

function NovaTransferenciaDialog({ locais, onClose, onSaved }: {
  locais: { id: number; descricao: string }[]; onClose: () => void; onSaved: () => void;
}) {
  const [origem, setOrigem] = useState<number | null>(null);
  const [destino, setDestino] = useState<number | null>(null);
  const [frete, setFrete] = useState(0);
  const [seguro, setSeguro] = useState(0);
  const [obs, setObs] = useState('');
  const [itens, setItens] = useState<ItemDraft[]>([{ produtoId: 0, qtdEmitida: 1, custoUnitEmissao: 0 }]);

  const criar = useMutation({
    mutationFn: () => svc.criar({
      localOrigemId: origem!, localDestinoId: destino!, valorFrete: frete, valorSeguro: seguro,
      observacao: obs || undefined,
      itens: itens.filter(i => i.produtoId > 0 && i.qtdEmitida > 0),
    }),
    onSuccess: (r) => { toast.success(r.mensagem); onSaved(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const podeSalvar = origem && destino && origem !== destino && itens.some(i => i.produtoId > 0 && i.qtdEmitida > 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Nova Transferência</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Local de origem</Label>
              <Select value={origem ? String(origem) : SENT} onValueChange={(v) => setOrigem(v === SENT ? null : Number(v))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{locais.map(l => <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Local de destino</Label>
              <Select value={destino ? String(destino) : SENT} onValueChange={(v) => setDestino(v === SENT ? null : Number(v))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{locais.map(l => <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Frete (R$)</Label><Input type="number" value={frete} onChange={e => setFrete(Number(e.target.value))} /></div>
            <div><Label>Seguro/outros (R$)</Label><Input type="number" value={seguro} onChange={e => setSeguro(Number(e.target.value))} /></div>
          </div>
          {origem !== null && destino !== null && origem === destino && (
            <p className="text-sm text-destructive">Origem e destino devem ser diferentes.</p>
          )}
          <div><Label>Observação</Label><Textarea value={obs} onChange={e => setObs(e.target.value)} rows={2} /></div>

          <div className="space-y-2">
            <div className="flex items-center justify-between"><Label>Itens</Label>
              <Button size="sm" variant="outline" onClick={() => setItens([...itens, { produtoId: 0, qtdEmitida: 1, custoUnitEmissao: 0 }])}>
                <Plus className="h-4 w-4" />Item
              </Button>
            </div>
            {itens.map((it, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_110px_130px_40px] gap-2 items-center">
                <ProdutoSearchCombobox value={it.produtoId || undefined}
                  onChange={(v) => setItens(itens.map((x, i) => i === idx ? { ...x, produtoId: v } : x))} />
                <Input type="number" placeholder="Qtd" value={it.qtdEmitida}
                  onChange={e => setItens(itens.map((x, i) => i === idx ? { ...x, qtdEmitida: Number(e.target.value) } : x))} />
                <Input type="number" placeholder="Custo unit." value={it.custoUnitEmissao}
                  onChange={e => setItens(itens.map((x, i) => i === idx ? { ...x, custoUnitEmissao: Number(e.target.value) } : x))} />
                <Button size="icon" variant="ghost" onClick={() => setItens(itens.filter((_, i) => i !== idx))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">Custo unit. 0 = usa o custo médio do local de origem.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!podeSalvar || criar.isPending} onClick={() => criar.mutate()}>Criar transferência</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detalhe + workflow ──────────────────────────────────────────────────────

function DetalheDialog({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void; }) {
  const { data: t, refetch } = useQuery({ queryKey: ['transferencia', id], queryFn: () => svc.obter(id) });
  const [conf, setConf] = useState<Record<number, { qtd: number; motivo: number; obs: string }>>({});
  const [motivoCancel, setMotivoCancel] = useState('');

  const after = (msg: string) => { toast.success(msg); refetch(); onChanged(); };
  const err = (e: unknown) => toast.error(getApiErrorMessage(e));
  const mAprovar = useMutation({ mutationFn: () => svc.aprovar(id), onSuccess: (r) => after(r.mensagem), onError: err });
  const mDespachar = useMutation({ mutationFn: () => svc.despachar(id), onSuccess: (r) => after(r.mensagem), onError: err });
  const mConcluir = useMutation({ mutationFn: () => svc.concluir(id), onSuccess: (r) => after(r.mensagem), onError: err });
  const mCancelar = useMutation({ mutationFn: () => svc.cancelar(id, motivoCancel), onSuccess: (r) => after(r.mensagem), onError: err });
  const mConf = useMutation({
    mutationFn: () => svc.conferencia(id, {
      itens: Object.entries(conf).map(([itemId, c]) => ({
        itemId: Number(itemId), qtdRecebida: c.qtd, motivoDivergencia: c.motivo, observacaoDivergencia: c.obs || undefined,
      })),
    }),
    onSuccess: (r) => after(r.mensagem), onError: err,
  });

  const setC = (it: TransferenciaItem, patch: Partial<{ qtd: number; motivo: number; obs: string }>) =>
    setConf(prev => ({ ...prev, [it.id]: { qtd: prev[it.id]?.qtd ?? it.qtdRecebida ?? it.qtdEmitida, motivo: prev[it.id]?.motivo ?? it.motivoDivergenciaId, obs: prev[it.id]?.obs ?? '', ...patch } }));

  if (!t) return null;
  const emTransito = t.statusId === 2;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2">
          Transferência #{t.numero}<Badge className={STATUS_BADGE[t.statusId]}>{t.status}</Badge>
        </DialogTitle></DialogHeader>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-muted-foreground">Origem:</span> {t.localOrigem}</div>
          <div><span className="text-muted-foreground">Destino:</span> {t.localDestino}</div>
          <div><span className="text-muted-foreground">Valor:</span> R$ {fmt(t.valorTotal)}</div>
          <div><span className="text-muted-foreground">Frete/Seguro:</span> R$ {fmt(t.valorFrete)} / R$ {fmt(t.valorSeguro)}</div>
          {t.observacao && <div className="col-span-2"><span className="text-muted-foreground">Obs:</span> {t.observacao}</div>}
          {t.motivoCancelamento && <div className="col-span-2 text-destructive">Cancelada: {t.motivoCancelamento}</div>}
        </div>

        <Table>
          <TableHeader><TableRow>
            <TableHead>Produto</TableHead><TableHead className="text-right">Emitida</TableHead>
            <TableHead className="text-right">Custo dest.</TableHead>
            {emTransito ? <><TableHead className="w-28 text-right">Recebida</TableHead><TableHead className="w-48">Motivo diverg.</TableHead></>
              : <><TableHead className="text-right">Recebida</TableHead><TableHead className="text-right">Diverg.</TableHead></>}
          </TableRow></TableHeader>
          <TableBody>
            {t.itens.map(it => (
              <TableRow key={it.id}>
                <TableCell>{it.produto ?? `Produto ${it.produtoId}`}</TableCell>
                <TableCell className="text-right">{fmt(it.qtdEmitida)}</TableCell>
                <TableCell className="text-right">R$ {fmt(it.custoUnitDestino)}</TableCell>
                {emTransito ? (
                  <>
                    <TableCell>
                      <Input type="number" className="h-8 text-right"
                        value={conf[it.id]?.qtd ?? it.qtdEmitida}
                        onChange={e => setC(it, { qtd: Number(e.target.value) })} />
                    </TableCell>
                    <TableCell>
                      <Select value={String(conf[it.id]?.motivo ?? it.motivoDivergenciaId)} onValueChange={(v) => setC(it, { motivo: Number(v) })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{MOTIVOS_DIVERGENCIA.map(m => <SelectItem key={m.id} value={String(m.id)}>{m.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className="text-right">{fmt(it.qtdRecebida)}</TableCell>
                    <TableCell className={`text-right ${it.qtdDivergencia < 0 ? 'text-destructive' : it.qtdDivergencia > 0 ? 'text-amber-600' : ''}`}>{fmt(it.qtdDivergencia)}</TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {emTransito && (
          <Button variant="outline" size="sm" onClick={() => mConf.mutate()} disabled={mConf.isPending}>Salvar conferência</Button>
        )}

        {(t.statusId === 0 || t.statusId === 1) && (
          <div className="flex items-end gap-2 border-t pt-3">
            <div className="flex-1"><Label className="text-xs">Motivo (p/ cancelar)</Label>
              <Input value={motivoCancel} onChange={e => setMotivoCancel(e.target.value)} placeholder="Motivo do cancelamento" /></div>
            <Button variant="destructive" disabled={!motivoCancel || mCancelar.isPending} onClick={() => mCancelar.mutate()}>Cancelar transf.</Button>
          </div>
        )}

        <DialogFooter className="gap-2">
          {t.statusId === 1 && <Button onClick={() => mAprovar.mutate()} disabled={mAprovar.isPending}>Aprovar</Button>}
          {t.statusId === 0 && <Button onClick={() => mDespachar.mutate()} disabled={mDespachar.isPending}>Despachar (baixa origem)</Button>}
          {emTransito && <Button onClick={() => mConcluir.mutate()} disabled={mConcluir.isPending}>Concluir recebimento (entra destino)</Button>}
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
